import React from "react";
import "./YearRoulette.css";

type Props = {
  year: number;
  onChange: (year: number) => void;
  minYear?: number;
  maxYear?: number;
};

const DEG_PER_YEAR = 32;
const INERTIA = 0.88;
const STOP_OMEGA = 0.06;
const VISIBLE_RANGE = 3;

export const YearRoulette: React.FC<Props> = ({
  year,
  minYear = 1900,
  maxYear = 2100,
  onChange,
}) => {
  const [angle, setAngle] = React.useState(0);
  const [omega, setOmega] = React.useState(0);

  const draggingRef = React.useRef(false);
  const lastMouseYRef = React.useRef(0);
  const lastEmittedYearRef = React.useRef<number | null>(null);
  const MIN_ANGLE = (year - maxYear) * DEG_PER_YEAR;
  const MAX_ANGLE = (year - minYear) * DEG_PER_YEAR;

  function clamp(value: number, min: number, max: number) {
    return Math.min(max, Math.max(min, value));
  }

  React.useEffect(() => {
    let raf: number;

    const loop = () => {
      if (!draggingRef.current) {
        const snapAngle = Math.round(angle / DEG_PER_YEAR) * DEG_PER_YEAR;

        let newOmega = omega * INERTIA;
        let newAngle = angle + newOmega;

        if (newAngle < MIN_ANGLE || newAngle > MAX_ANGLE) {
          newAngle = clamp(newAngle, MIN_ANGLE, MAX_ANGLE);
          newOmega *= -0.2;
        }

        if (Math.abs(newOmega) < STOP_OMEGA) {
          newOmega = 0;
          newAngle = snapAngle;
        }

        setOmega(newOmega);
        setAngle(newAngle);
      }

      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [MAX_ANGLE, MIN_ANGLE, angle, omega]);

  const onMouseDown = (e: React.MouseEvent) => {
    draggingRef.current = true;
    lastMouseYRef.current = e.clientY;
    setOmega(0);
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!draggingRef.current) return;

    const dy = lastMouseYRef.current - e.clientY;
    lastMouseYRef.current = e.clientY;

    const deltaAngle = dy * 0.6;

    setAngle((a) => clamp(a + deltaAngle, MIN_ANGLE, MAX_ANGLE));

    setOmega(deltaAngle * 1.8);
  };

  const onMouseUp = () => {
    draggingRef.current = false;
  };

  const currentYear = year - Math.round(angle / DEG_PER_YEAR);

  React.useEffect(() => {
    if (omega === 0) {
      const settledYear = year - Math.round(angle / DEG_PER_YEAR);

      if (lastEmittedYearRef.current !== settledYear) {
        lastEmittedYearRef.current = settledYear;
        onChange(settledYear);
      }
    }
  }, [omega, angle, year, onChange]);

  const years = [];
  for (let i = -VISIBLE_RANGE; i <= VISIBLE_RANGE; i++) {
    const y = currentYear + i;
    if (y < minYear || y > maxYear) continue;

    const itemAngle = (year - y) * DEG_PER_YEAR;
    const delta = itemAngle - angle;

    const translateY = delta * 1.1;
    const rotateX = delta * 0.7;
    const depth = Math.min(160, Math.abs(delta) * 1.8);
    const translateZ = -depth;

    const scale = Math.max(0.65, 1 - Math.abs(delta) / 140);
    const opacity = Math.max(0.25, 1 - Math.abs(delta) / 100);

    years.push(
      <div
        key={y}
        style={{
          position: "absolute",
          transform: `
            translateY(${translateY}px)
            translateZ(${translateZ}px)
            rotateX(${rotateX}deg)
            scale(${scale})
            `,
          opacity,
          transition: draggingRef.current ? "none" : "transform 0.18s ease-out",
          fontWeight: y === currentYear ? 700 : 400,
          transformStyle: "preserve-3d",
          willChange: "transform",
        }}
      >
        {y}
      </div>,
    );
  }

  return (
    <div
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      className="yearRouletteContainer"
    >
      {years}
    </div>
  );
};
